/**
 * Glue between the screens, the API, the voice backend and the store.
 * One voice session for the whole app.
 */
import {
  ApiErrorCode,
  PRIORITY_SPEAKER_DUCK,
  VOICE_WS_PATH,
  VoiceCloseCodes,
  type NoiseSuppression,
  type Pid,
  type Snowflake,
  type VoiceMediaClaims,
  type VoiceTokenClaims,
  type VoiceTokenResponse,
} from '@neosocial/protocol';
import { api, isApiRequestError, NetworkError } from '../lib/api';
import { decodeJwtPayload } from '../lib/api/tokens';
import { desktopBridge, wsUrl } from '../lib/config';
import { isIOSWebKit } from '../lib/device';
import { useData } from '../store/data';
import { flagOn, onConfigChange, useConfig } from '../store/config';
import {
  duckedPids,
  errorFromClose,
  errorFromDeny,
  myVoiceBlocked,
  pidOfUser,
  type VoiceAction,
  type VoiceError,
} from './machine';
import { liveAttach, liveDetach, liveServer } from './live';
import { bindHotkeys } from './hotkeys';
import { bindPtt, toDesktopPttCode, type PttBinding, type PttMode } from './ptt';
import { useVoice } from './store';
import {
  VoiceDeniedError,
  clampVolume,
  sendBitrate,
  type JoinError,
  type ServerMessage,
  type Voice,
} from './types';
import { createWebVoice } from './webVoice';
import { effectiveNoise } from './noise';

let factory: () => Voice = () => createWebVoice();
/** Tests swap the backend. */
export function setVoiceFactory(f: () => Voice): void {
  factory = f;
}

/**
 * Waits before each automatic reconnect attempt after the connection to
 * comms-voice dropped (HV-B01): quick first (a comms-voice restart takes a
 * fraction of a second), then backing off, about 25 s in all.
 */
let reconnectDelays = [250, 1000, 2000, 4000, 8000, 8000];
/** Tests shorten the waits. */
export function setReconnectDelays(ms: number[]): void {
  reconnectDelays = ms;
}
/** Failures a reconnect attempt retries (the voice server is away); others are final. */
const RETRYABLE = new Set<VoiceError>(['no_server', 'unknown', 'media', 'lost']);

let voice: Voice | null = null;
let offs: Array<() => void> = [];
let claims: (VoiceTokenClaims & VoiceMediaClaims) | null = null;
let ptt: PttBinding | null = null;
/** The bound push-to-talk key's press and release, for the touch hold button (`pttHold`). */
let pttTouch: { down(): void; up(): void } | null = null;
let vaOpenUntil = 0;
let releaseTimer: ReturnType<typeof setTimeout> | undefined;
/** The push-to-talk key and the priority-speaker key are held (2026-11-01). */
let pttDown = false;
let prioDown = false;
let prio: PttBinding | null = null;
/** The pids played lowered now, under a priority speaker. */
let ducked = new Set<Pid>();
let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
let joinSeq = 0;
/** Channel passwords typed this session (memory only): a reconnect needs them again. */
const passwords = new Map<Snowflake, string>();
/** The bitrate the current backend was told (HV-B04). */
let appliedBitrate: number | null = null;
/** What the current backend was told about being blocked (`myVoiceBlocked`). */
let appliedBlocked = false;
/** The last join from joinVoice opened the system default for a missing microphone (told once). */
let lastMicFallback = false;

export class VoiceJoinError extends Error {
  constructor(
    readonly code: VoiceError,
    /** The server's refusal behind it, when there was one (a staff limit says until when). */
    readonly refusal?: unknown,
  ) {
    super(code);
    this.name = 'VoiceJoinError';
  }
}

const act = (a: VoiceAction) => useVoice.getState().act(a);

/** comms-voice moved you because a permission change took your channel away. */
export interface LostAccessMove {
  from: Snowflake;
  to: Snowflake;
}
const lostAccessListeners = new Set<(m: LostAccessMove) => void>();
/**
 * Called when comms-voice moves you out of a channel you lost access to (a
 * role or overwrite took Connect or View Channel away) into the default
 * channel. QA 2026-10-28: that move was silent, only the "move" sound.
 * Returns the unsubscribe.
 */
export function onLostAccessMove(fn: (m: LostAccessMove) => void): () => void {
  lostAccessListeners.add(fn);
  return () => lostAccessListeners.delete(fn);
}

function tokenErrorCode(e: unknown): VoiceError {
  if (e instanceof NetworkError) return 'no_server';
  if (isApiRequestError(e)) {
    switch (e.code) {
      case ApiErrorCode.CHANNEL_PASSWORD_REQUIRED:
        return 'password_required';
      case ApiErrorCode.CHANNEL_PASSWORD_WRONG:
        return 'bad_password';
      case ApiErrorCode.CHANNEL_FULL:
        return 'full';
      case ApiErrorCode.CHANNEL_BANNED:
        return 'banned';
      // CANNOT_MESSAGE_USER: a DM call to someone you can't reach (a block
      // either way, their DM privacy, a pending request) is refused, never
      // "something went wrong" (QA NV-04)
      case ApiErrorCode.MISSING_PERMISSIONS:
      case ApiErrorCode.MISSING_ACCESS:
      case ApiErrorCode.MEMBER_TIMED_OUT:
      case ApiErrorCode.CANNOT_MESSAGE_USER:
        return 'not_allowed';
      case ApiErrorCode.UNAUTHORIZED:
      case ApiErrorCode.INVALID_TOKEN:
        return 'auth';
    }
  }
  return 'unknown';
}

/**
 * Why a join ended before `welcome`: comms-voice's own reason when it
 * refused (a full channel is "full", never "not allowed", HV-B03), else by
 * the close code.
 */
function joinErrorCode(e: JoinError): VoiceError {
  if (e.code === VoiceCloseCodes.AUTH_FAILED) return 'auth';
  if (e.code === VoiceCloseCodes.FORBIDDEN) {
    const why = e.reason ? errorFromDeny(e.reason) : 'unknown';
    return why === 'unknown' ? 'not_allowed' : why;
  }
  // replaced, a moderator's disconnect, left from elsewhere, or lost access
  // (LOST_ACCESS: a permission change, never "a moderator disconnected you")
  const closed = e.code === undefined ? 'lost' : errorFromClose(e.code);
  return closed === 'lost' ? 'no_server' : closed;
}

/**
 * iOS runs a hidden page's timers once a second, too slow for voice
 * activity to follow the voice, so the mic went quiet as soon as the
 * phone locked (owner, 2026-11-03): while hidden there it stays open, and
 * the level decides again once the page is back in view.
 */
function heldInBackground(): boolean {
  return isIOSWebKit() && typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

function wire(v: Voice): void {
  if (typeof document !== 'undefined') {
    const onVisibility = () => {
      const { prefs, session } = useVoice.getState();
      if (voice !== v || prefs.inputMode !== 'va' || session.listenOnly) return;
      if (heldInBackground() && !session.transmitting) {
        v.transmit(true);
        act({ type: 'transmit', on: true });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    offs.push(() => document.removeEventListener('visibilitychange', onVisibility));
  }
  offs.push(
    v.on('state', (e) => {
      const s = useVoice.getState().session;
      if (e.state === 'failed' && voice === v) {
        // a join that fails before `welcome` is reported by start()
        if (s.pid === null) return;
        // the media path died under a working connection (a network
        // change): reconnect, as when the socket drops
        if (s.status === 'connected' || s.reconnecting > 0) return lost(v);
      }
      act({ type: 'media', state: e.state });
    }),
    v.on('levels', (e) => {
      const { prefs, session } = useVoice.getState();
      if (prefs.inputMode === 'va' && !session.listenOnly) {
        const now = Date.now();
        if (e.input >= prefs.sensitivity) vaOpenUntil = now + 400;
        // the priority-speaker key holds the gate open, as push-to-talk does
        const open = now < vaOpenUntil || prioDown || heldInBackground();
        if (open !== session.transmitting) {
          v.transmit(open);
          act({ type: 'transmit', on: open });
        }
      }
      act({ type: 'levels', ev: e, now: Date.now() });
    }),
    v.on('signal_closed', (e) => {
      if (voice !== v) return;
      // Replaced by another connection of yours, a moderator disconnected you,
      // you left from another device (LEFT) or lost access to the channel
      // (LOST_ACCESS): final, each with its own words. Anything else (a
      // network blip, a comms-voice restart) reconnects by itself (HV-B01).
      const error = errorFromClose(e.code);
      if (error !== 'lost') {
        act({ type: 'error', error, message: e.reason });
        dropBackend();
        return;
      }
      lost(v);
    }),
    v.on('*', (e) => {
      if (e.t === 'levels' || e.t === 'state' || e.t === 'signal_closed' || e.t === 'welcome')
        return;
      // Deltas that arrive before the welcome is applied (the SDP answer is
      // still being set) wait for it; applied first, the welcome's roster
      // would overwrite them. Live bug 2026-10-28: a `left` for your old
      // connection was lost this way and "you" stayed twice.
      if (early) early.push(e);
      else onServerEvent(e);
    }),
  );
}

let early: ServerMessage[] | null = null;

function onServerEvent(e: ServerMessage): void {
  // comms-voice going away (a deploy or restart) or an expired token: reconnect
  if (
    e.t === 'bye' &&
    (e.reason === 'shutdown' || e.reason === 'error' || e.reason === 'token_expired')
  ) {
    if (voice) lost(voice);
    return;
  }
  const from = useVoice.getState().session.channelId;
  act({ type: 'server', msg: e });
  if (e.t === 'moved' && e.reason === 'lost_access' && from && from !== e.channel)
    for (const fn of lostAccessListeners) fn({ from, to: e.channel });
  if (e.t === 'member') applyPerUser(e.user_id);
  // your screen share and camera follow (a move ends the share, the camera comes back)
  liveServer(e);
  if (e.t === 'bye') dropBackend();
}

/** Applies the events held back while the welcome was on its way. */
function flushEarly(): void {
  const q = early ?? [];
  early = null;
  for (const e of q) onServerEvent(e);
}

/** Re-applies a person's listener mute and volume to their current pid. */
function applyPerUser(userId: Snowflake): void {
  const s = useVoice.getState().session;
  const pid = pidOfUser(s, userId);
  if (pid === null || !voice) return;
  if (s.localMutes[userId]) voice.localMute(pid, true);
  const vol = s.volumes[userId];
  if (vol !== undefined || ducked.has(pid)) voice.setVolume(pid, playVolume(pid, vol ?? 1));
}

/** A person's volume as played: lowered while a priority speaker talks (Discord). */
function playVolume(pid: Pid, base: number): number {
  return ducked.has(pid) ? base * PRIORITY_SPEAKER_DUCK : base;
}

/**
 * Priority speaker (2026-11-01): while one talks to you, everyone else
 * plays lowered (`PRIORITY_SPEAKER_DUCK` of their own volume), and back
 * when they stop.
 */
function syncDucking(): void {
  const v = voice;
  const s = useVoice.getState().session;
  const next = (v && duckedPids(s)) || new Set<Pid>();
  if (next.size === ducked.size && [...next].every((p) => ducked.has(p))) return;
  const changed = new Set([...next, ...ducked]);
  ducked = next;
  if (!v) return;
  for (const p of changed) {
    const userId = s.members[p]?.user_id;
    const base = userId !== undefined ? s.volumes[userId] : undefined;
    v.setVolume(p, playVolume(p, base ?? 1));
  }
}

function dropBackend(): void {
  for (const off of offs) off();
  offs = [];
  early = null;
  const v = voice;
  voice = null;
  claims = null;
  appliedBitrate = null;
  appliedBlocked = false;
  liveDetach();
  v?.leave();
  ptt?.dispose();
  ptt = null;
  pttTouch = null;
  prio?.dispose();
  prio = null;
  pttDown = prioDown = false;
  ducked = new Set();
}

/* ----------------------------- reconnecting ----------------------------- */

/**
 * The connection to comms-voice dropped under a working session: reach it
 * again by itself, with a fresh token, into the same channel (HV-B01). The
 * UI says "reconnecting"; others see you leave and come back.
 */
function lost(v: Voice): void {
  if (voice !== v) return;
  const s = useVoice.getState().session;
  const channelId = s.channelId ?? s.targetId;
  const guildId = s.guildId;
  // a reconnect that drops again before it is up counts on, never from 1
  const attempt = s.reconnecting + 1;
  dropBackend();
  if (!channelId || !guildId || attempt > reconnectDelays.length) {
    act({ type: 'error', error: 'lost' });
    return;
  }
  retry(channelId, guildId, attempt, ++joinSeq);
}

function retry(channelId: Snowflake, guildId: Snowflake, attempt: number, seq: number): void {
  act({ type: 'reconnecting', attempt, channelId });
  clearTimeout(reconnectTimer);
  reconnectTimer = setTimeout(
    () => void reconnect(channelId, guildId, attempt, seq),
    reconnectDelays[attempt - 1] ?? reconnectDelays.at(-1) ?? 1000,
  );
}

async function reconnect(
  channelId: Snowflake,
  guildId: Snowflake,
  attempt: number,
  seq: number,
): Promise<void> {
  if (seq !== joinSeq) return;
  const password = passwords.get(channelId);
  try {
    const token = await requestToken(channelId, password);
    if (seq !== joinSeq) return;
    act({ type: 'token' });
    await start(channelId, token, { password, quiet: true });
  } catch (e) {
    if (seq !== joinSeq) return;
    const code = e instanceof VoiceJoinError ? e.code : 'unknown';
    if (RETRYABLE.has(code) && attempt < reconnectDelays.length)
      retry(channelId, guildId, attempt + 1, seq);
    else act({ type: 'error', error: RETRYABLE.has(code) ? 'lost' : code });
  }
}

/* ------------------------------- joining ------------------------------- */

async function requestToken(channelId: Snowflake, password?: string): Promise<VoiceTokenResponse> {
  try {
    return await api.voice.token({ channel_id: channelId, ...(password ? { password } : {}) });
  } catch (e) {
    throw new VoiceJoinError(tokenErrorCode(e), e);
  }
}

/** The channel's bitrate (bit/s) as the server has it, or null. */
function channelBitrate(id: Snowflake | null | undefined): number | null {
  const b = id ? useData.getState().channels[id]?.bitrate : null;
  return typeof b === 'number' && b > 0 ? b : null;
}

/** The console's `limits.voice_bitrate_kbps` in bit/s, from the live config, or null. */
function consoleBitrate(): number | null {
  const kbps = useConfig.getState().config?.settings['limits.voice_bitrate_kbps'];
  return typeof kbps === 'number' && kbps > 0 ? kbps * 1000 : null;
}

/**
 * The most the microphone sends in channel `id` (bit/s): the channel's
 * bitrate (HV-B04), held under the console's voice bitrate, both as it is
 * live and as the voice token carried it (comms-voice's answer holds the
 * browser to the token's until the next join). Bandwidth, 2026-10-29.
 */
function targetBitrate(id: Snowflake | null | undefined): number | null {
  return sendBitrate(channelBitrate(id), claims?.max_bitrate, consoleBitrate());
}

/**
 * Tells the backend the bitrate for the channel you are in now (moves,
 * live edits of the channel or of the console's cap).
 */
function syncBitrate(): void {
  const v = voice;
  if (!v) return;
  const b = targetBitrate(useVoice.getState().session.channelId);
  if (b === null || b === appliedBitrate) return;
  appliedBitrate = b;
  v.setBitrate(b);
}

/**
 * No packets while nobody may hear you (a server or channel mute or
 * deafen, no Speak where you are), and back when that lifts: the roster's
 * own `mute` events say when, live (bandwidth, 2026-10-29). Self-mute and
 * deafen keep their own path (`setSelfMute`, `setSelfDeaf`).
 */
function syncBlocked(): void {
  const v = voice;
  if (!v) return;
  const b = myVoiceBlocked(useVoice.getState().session);
  if (b === appliedBlocked) return;
  appliedBlocked = b;
  v.block(b);
}
useData.subscribe((d, p) => {
  if (d.channels !== p.channels) syncBitrate();
});
useVoice.subscribe((st, p) => {
  if (st.session.channelId !== p.session.channelId) syncBitrate();
  if (st.session.members !== p.session.members || st.session.pid !== p.session.pid) syncBlocked();
  if (
    st.session.priority !== p.session.priority ||
    st.session.talking !== p.session.talking ||
    st.session.members !== p.session.members
  )
    syncDucking();
});
onConfigChange((next, prev) => {
  if (next.settings['limits.voice_bitrate_kbps'] !== prev?.settings['limits.voice_bitrate_kbps'])
    syncBitrate();
});

/**
 * Starts a backend with a granted token and joins. `quiet`: a failure is
 * only thrown (a reconnect attempt decides what to show).
 */
async function start(
  channelId: Snowflake,
  token: VoiceTokenResponse,
  o: { password?: string; quiet?: boolean },
): Promise<void> {
  claims = decodeJwtPayload<VoiceTokenClaims & VoiceMediaClaims>(token.token);
  const v = factory();
  voice = v;
  early = [];
  wire(v);
  const { prefs, session } = useVoice.getState();
  const bitrate = targetBitrate(channelId);
  appliedBitrate = bitrate;
  try {
    const res = await v.join({
      token: token.token,
      url: wsUrl(token.ws_path || VOICE_WS_PATH),
      iceServers: token.ice_servers as RTCIceServer[],
      selfMute: session.selfMute,
      selfDeaf: session.selfDeaf,
      inputDeviceId: prefs.inputDeviceId,
      outputDeviceId: prefs.outputDeviceId,
      ...(prefs.outputVolume !== 1 ? { outputVolume: prefs.outputVolume } : {}),
      noiseSuppression: noiseMode(),
      bitrate,
      ...(o.password ? { password: o.password } : {}),
    });
    if (voice !== v) return;
    if (o.password) passwords.set(channelId, o.password);
    act({
      type: 'welcome',
      pid: res.pid,
      channel: res.channel,
      members: res.members,
      listenOnly: res.listenOnly,
      micFallback: !!res.micFallback,
    });
    flushEarly();
    if (voice !== v) return;
    if (v.state === 'connected') act({ type: 'media', state: 'connected' });
    for (const m of res.members) applyPerUser(m.user_id);
    setupInput();
    syncBitrate();
    // the camera comes back on, and after a reconnect a still-live screen share
    liveAttach(v, claims, { reconnect: !!o.quiet });
  } catch (e) {
    if (voice !== v) return;
    dropBackend();
    const err = e as JoinError;
    const code = joinErrorCode(err);
    if (!o.quiet) act({ type: 'error', error: code, message: err.message });
    throw new VoiceJoinError(code);
  }
}

/** The channels the current token lets us move to without a new token. */
export function canMoveWithoutToken(channelId: Snowflake): boolean {
  return !!(voice && claims && claims.channels.includes(channelId));
}

/**
 * Joins a voice channel, or moves inside the current session when the
 * token allows it. Throws `VoiceJoinError` ("password_required",
 * "bad_password", "full"...) so the screen can ask for a password or say why.
 */
export async function joinVoice(
  channelId: Snowflake,
  o: { guildId: Snowflake; password?: string },
): Promise<void> {
  const s = useVoice.getState().session;
  lastMicFallback = false;
  if (voice && s.channelId === channelId && s.status !== 'failed') return;
  if (
    voice &&
    (s.status === 'connected' || s.status === 'connecting') &&
    canMoveWithoutToken(channelId)
  ) {
    act({ type: 'moving', channelId });
    try {
      await voice.move(channelId, o.password);
    } catch (e) {
      act({ type: 'move_failed' });
      if (e instanceof VoiceDeniedError) {
        const code = errorFromDeny(e.reason);
        throw new VoiceJoinError(
          code === 'bad_password' && !o.password ? 'password_required' : code,
        );
      }
      throw new VoiceJoinError('unknown');
    }
    if (o.password) passwords.set(channelId, o.password);
    return;
  }
  // A fresh session. While you are in a room, the token comes first: a
  // refusal (a password to type, a wrong one, a full channel) leaves you
  // where you are, as Discord does; only a granted join moves you.
  const inRoom = !!voice && s.status !== 'idle' && s.status !== 'failed';
  const seq = ++joinSeq;
  clearTimeout(reconnectTimer);
  if (!inRoom) {
    dropBackend();
    act({ type: 'join', channelId, guildId: o.guildId });
  }
  let token;
  try {
    token = await requestToken(channelId, o.password);
  } catch (e) {
    const code = e instanceof VoiceJoinError ? e.code : 'unknown';
    if (seq === joinSeq && !inRoom) {
      if (code === 'password_required' || code === 'bad_password') act({ type: 'leave' });
      else act({ type: 'error', error: code });
    }
    throw e instanceof VoiceJoinError ? e : new VoiceJoinError(code, e);
  }
  if (seq !== joinSeq) return;
  if (inRoom) {
    dropBackend();
    act({ type: 'join', channelId, guildId: o.guildId });
  }
  act({ type: 'token' });
  await start(channelId, token, { password: o.password });
  // said once, after this join (never again on a move or a reconnect)
  lastMicFallback = useVoice.getState().session.micFallback;
}

export function leaveVoice(): void {
  joinSeq++;
  clearTimeout(reconnectTimer);
  dropBackend();
  act({ type: 'leave' });
}

// The console turned the global push-to-talk key on or off: bind it again.
onConfigChange((next, prev) => {
  if (prev && next.flags.desktop_global_ptt !== prev.flags.desktop_global_ptt) setupInput();
  // the keybinds are bound at start, before any config: the first one counts too
  if (flagOn('desktop_global_ptt', next) !== flagOn('desktop_global_ptt', prev ?? null))
    setupHotkeys();
});

/**
 * The desktop shell's global key hook follows the input mode, in voice or
 * not: voice activation removes it (`setPttKey(null)`; the shell would
 * otherwise keep it, and install it again at every launch: QA DT-BUG-07);
 * so does the console's `desktop_global_ptt` turned off (CO-050). Push-to-talk
 * outside voice keeps the shell on the chosen key (in voice, `bindPtt` sets it).
 */
export function syncShellPttKey(): void {
  const bridge = desktopBridge();
  if (!bridge?.pttSupported) return;
  const { prefs } = useVoice.getState();
  let code: string | null;
  if (prefs.inputMode !== 'ptt' || !flagOn('desktop_global_ptt')) code = null;
  else if (!voice) code = toDesktopPttCode(prefs.pttKey);
  else return;
  bridge.setPttKey(code).catch((err: unknown) => {
    console.warn('the desktop app refused the push-to-talk key:', err);
  });
}

/** The gate opens at once (push-to-talk or the priority-speaker key went down). */
function gateDown(): void {
  clearTimeout(releaseTimer);
  voice?.transmit(true);
  act({ type: 'transmit', on: true });
}

/** Both keys are up: the gate shuts after the release delay (push-to-talk only). */
function gateUp(): void {
  if (pttDown || prioDown) return;
  clearTimeout(releaseTimer);
  releaseTimer = setTimeout(() => {
    if (pttDown || prioDown) return;
    voice?.transmit(false);
    act({ type: 'transmit', on: false });
  }, useVoice.getState().prefs.pttReleaseMs);
}

/**
 * The noise suppression a join or the microphone uses now: the chosen one,
 * Strong only while the `noise_suppression` flag is on and the browser can
 * run it (voice/noise.ts).
 */
export function noiseMode(): NoiseSuppression {
  return effectiveNoise(useVoice.getState().prefs.noiseSuppression, flagOn('noise_suppression'));
}

/** Applies a changed noise suppression to the call; resolves with what runs (null: not in a call). */
export async function applyNoiseSuppression(): Promise<NoiseSuppression | null> {
  const v = voice;
  if (!v?.setNoiseSuppression) return null;
  return v.setNoiseSuppression(noiseMode());
}

/**
 * The dock's quick noise-suppression toggle (Discord's voice panel): Off,
 * or back to the last choice that was not Off (Standard at first; Strong
 * again if that is what you had). Resolves with what runs, as
 * `applyNoiseSuppression`.
 */
export function toggleNoiseSuppression(): Promise<NoiseSuppression | null> {
  const { prefs, setPrefs } = useVoice.getState();
  if (prefs.noiseSuppression === 'off') setPrefs({ noiseSuppression: prefs.noiseOnMode });
  else setPrefs({ noiseSuppression: 'off', noiseOnMode: prefs.noiseSuppression });
  return applyNoiseSuppression();
}

/** The noise suppression the call runs now, or null when not in one. */
export function runningNoiseSuppression(): NoiseSuppression | null {
  return voice?.noiseSuppression ?? null;
}

/**
 * The priority-speaker key (Discord's "Push to Talk (Priority)",
 * 2026-11-01): held, you transmit (in either input mode) and comms-voice
 * tells everyone you are a priority speaker where you have Priority
 * Speaker. Works while this window is focused (the desktop shell's global
 * hook carries push-to-talk's key only).
 */
function setupPriority(): void {
  prio?.dispose();
  prio = null;
  prioDown = false;
  const v = voice;
  const code = useVoice.getState().prefs.priorityKey;
  if (!v || !code) return;
  prio = bindPtt({
    code,
    onDown: () => {
      prioDown = true;
      voice?.setPriority?.(true);
      const ch = useVoice.getState().session.channelId;
      act({ type: 'priority', on: !!ch && !!claims?.priority?.includes(ch) });
      gateDown();
    },
    onUp: () => {
      prioDown = false;
      voice?.setPriority?.(false);
      act({ type: 'priority', on: false });
      // push-to-talk: the release delay; voice activity: the levels decide
      if (useVoice.getState().prefs.inputMode === 'ptt') gateUp();
    },
  });
}

/** Binds push-to-talk or voice activity for the current session. */
export function setupInput(): void {
  ptt?.dispose();
  ptt = null;
  pttTouch = null;
  pttDown = false;
  syncShellPttKey();
  const v = voice;
  if (!v) return;
  setupPriority();
  const { prefs } = useVoice.getState();
  clearTimeout(releaseTimer);
  if (prefs.inputMode === 'ptt') {
    v.transmit(false);
    act({ type: 'transmit', on: false });
    const down = () => {
      pttDown = true;
      gateDown();
    };
    const up = () => {
      pttDown = false;
      gateUp();
    };
    ptt = bindPtt({
      code: prefs.pttKey,
      // the global (any window) key only while the console's desktop_global_ptt is on
      bridge:
        desktopBridge()?.pttSupported && flagOn('desktop_global_ptt') ? desktopBridge() : undefined,
      onDown: down,
      onUp: up,
    });
    pttTouch = { down, up };
  } else {
    // voice activity: the levels handler opens the gate
    v.transmit(false);
    act({ type: 'transmit', on: false });
  }
}

/**
 * Push to talk by touch (docs/design/visual-overhaul.md §12.7 B2): a phone or
 * tablet has no key to hold, so the voice room's hold button presses and
 * releases the key here, with the key's own release delay. Nothing happens
 * outside push to talk or out of voice.
 */
export function pttHold(on: boolean): void {
  if (!pttTouch) return;
  if (on) pttTouch.down();
  else pttTouch.up();
}

let hotkeysOff: (() => void) | null = null;

/** The keybinds go global through the shell's hook (desktop, `desktop_global_ptt` on). */
function globalHotkeys(): boolean {
  const b = desktopBridge();
  return !!(b?.pttSupported && b.setHotkeys && b.onHotkey && flagOn('desktop_global_ptt'));
}

/**
 * Toggle mute and Toggle deafen (Settings › Voice & audio › Keybinds), in
 * voice or not, as the dock's buttons (their sounds follow the state). Bound
 * at start and again when they or `desktop_global_ptt` change.
 */
export function setupHotkeys(): void {
  hotkeysOff?.();
  const { prefs } = useVoice.getState();
  const keys = { toggle_mute: prefs.muteKey, toggle_deafen: prefs.deafenKey };
  const b = desktopBridge();
  const global = globalHotkeys();
  // the shell keeps nothing global while the console's flag is off
  if (b?.setHotkeys && !global)
    b.setHotkeys({ toggle_mute: null, toggle_deafen: null }).catch(() => undefined);
  hotkeysOff = bindHotkeys({
    keys,
    run: (a) => (a === 'toggle_mute' ? toggleMute() : toggleDeaf()),
    bridge: global ? (b ?? undefined) : undefined,
  });
}

/** Where the keybinds work: everywhere (desktop hook) or only while focused. */
export function hotkeysMode(): PttMode {
  return globalHotkeys() ? 'desktop' : 'focused';
}

/** Where push-to-talk works: everywhere (desktop hook) or only while focused. */
export function pttMode(): PttMode {
  return desktopBridge()?.pttSupported ? 'desktop' : 'focused';
}

export function setSelfMute(on: boolean): void {
  act({ type: 'self', mute: on });
  voice?.mute(useVoice.getState().session.selfMute);
}

/**
 * Your sound on or off. Undeafening keeps the microphone mute you had
 * before deafening (Discord); `mute` sets it as well, in one step.
 */
export function setSelfDeaf(on: boolean, mute?: boolean): void {
  act(mute === undefined ? { type: 'self', deaf: on } : { type: 'self', deaf: on, mute });
  const s = useVoice.getState().session;
  voice?.deafen(s.selfDeaf);
  voice?.mute(s.selfMute);
}

/** The mic button: while deafened, a click undeafens and turns the microphone on (Discord). */
export function toggleMute(): void {
  const s = useVoice.getState().session;
  if (s.selfDeaf) setSelfDeaf(false, false);
  else setSelfMute(!s.selfMute);
}

export function toggleDeaf(): void {
  setSelfDeaf(!useVoice.getState().session.selfDeaf);
}

export function setLocalMute(userId: Snowflake, muted: boolean): void {
  act({ type: 'local_mute', userId, muted });
  const pid = pidOfUser(useVoice.getState().session, userId);
  if (pid !== null) voice?.localMute(pid, muted);
}

export function setUserVolume(userId: Snowflake, volume: number): void {
  act({ type: 'volume', userId, volume });
  const pid = pidOfUser(useVoice.getState().session, userId);
  // under a priority speaker the new volume plays lowered too
  if (pid !== null) voice?.setVolume(pid, playVolume(pid, volume));
}

/** The master output volume, 0..2 (the output menu's slider): kept, and played at once. */
export function setOutputVolume(volume: number): void {
  const v = clampVolume(volume);
  useVoice.getState().setPrefs({ outputVolume: v });
  voice?.setOutputVolume?.(v);
}

// AFK auto-move (2026-11-01): input in the app (keys, pointer, touch) tells
// comms-voice with the next ping that you are here. One flag per event,
// nothing sent until the ping.
if (typeof window !== 'undefined') {
  const note = () => voice?.noteInput?.();
  for (const ev of ['keydown', 'pointerdown', 'pointermove', 'wheel', 'touchstart'] as const)
    window.addEventListener(ev, note, { capture: true, passive: true });
}

// The console turned Strong noise suppression on or off: the call follows.
onConfigChange((next, prev) => {
  if (flagOn('noise_suppression', next) !== flagOn('noise_suppression', prev ?? null))
    void applyNoiseSuppression();
});

/**
 * Applies the chosen devices to the call. A microphone that opens ends
 * listen-only at once (HV-B08, ST-B05: picking a working microphone while
 * connected kept sending nothing until a rejoin); a chosen one that is gone
 * falls back to the default.
 */
export async function applyDevices(): Promise<void> {
  const v = voice;
  if (!v) return;
  const { prefs } = useVoice.getState();
  const mic = await v.setInputDevice(prefs.inputDeviceId);
  if (voice !== v) return;
  if (mic) {
    const { listenOnly: wasListenOnly, transmitting } = useVoice.getState().session;
    act({ type: 'mic', listenOnly: !mic.ok, fallback: mic.fallback });
    // push-to-talk or voice activity is bound again for the new microphone,
    // unless push-to-talk is held right now (the ring keeps following it)
    if (mic.ok && wasListenOnly && !transmitting) setupInput();
  }
  await v.setOutputDevice(prefs.outputDeviceId);
}

/** True once after a join that used the system default because the chosen microphone was missing. */
export function joinedWithDefaultMic(): boolean {
  const was = lastMicFallback;
  lastMicFallback = false;
  return was;
}

export function voiceBackend(): Voice | null {
  return voice;
}
